import { z } from 'zod';

/**
 * Mirrors `flagTypeEnum` and `redFlagStatusEnum` in db/schema/redFlags.ts.
 * BE only accepts `crisis | acute_risk` as flagType filter values today.
 */
export const flagTypeEnum = z.enum(['crisis', 'acute_risk']);
export type FlagType = z.infer<typeof flagTypeEnum>;

export const redFlagStatusEnum = z.enum(['open', 'under_review', 'closed']);
export type RedFlagStatus = z.infer<typeof redFlagStatusEnum>;

/**
 * BA-wide red flag row. BE returns RAW table columns with NO joins —
 * `client.firstName`, `practitionerName`, etc. are NOT present. Cells that
 * need names show the ID as a clickable link to that resource's detail.
 * Mirrors `redFlagService.listFlags` SELECT in elixaa-api.
 */
export const redFlagSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  sessionId: z.string().nullish(),
  raisedById: z.string(),
  flagType: flagTypeEnum,
  status: redFlagStatusEnum,
  description: z.string().nullable(),
  assignedToId: z.string().nullish(),
  actionTaken: z.string().nullish(),
  resolvedAt: z.string().nullish(),
  resolvedById: z.string().nullish(),
  // BE doesn't return businessAssociateId; platform surface infers from client BA.
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  // Denormalized joins from the BE — the practitioner who raised the flag
  // and the one who resolved it (when applicable). Optional / nullish so
  // older clients reading rows authored before the BE join shipped still
  // parse cleanly.
  raisedByName: z.string().nullish(),
  resolvedByName: z.string().nullish(),
});
export type RedFlag = z.infer<typeof redFlagSchema>;

export interface RedFlagListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  flagType?: FlagType;
  status?: RedFlagStatus;
  search?: string;
  organizationId?: string;
  locationId?: string;
}

export interface PaginatedRedFlags {
  data: RedFlag[];
  pagination: { page: number; limit: number; total: number };
}

export const raiseRedFlagSchema = z.object({
  flagType: flagTypeEnum,
  description: z.string().min(1).max(5000),
  sessionId: z.string().max(50).optional(),
});
export type RaiseRedFlagInput = z.infer<typeof raiseRedFlagSchema>;
